import z from 'zod';

import { UnityParagraph } from './Paragraph';
import { PlateTextElements, TextElementsCodec } from './Text';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES, UNITY_ELEMENT_TYPES } from '../constants';

const UnityPollItem = z.object({
    id: z.string(),
    text: UnityParagraph,
    version: Version,
    type: z.literal(UNITY_ELEMENT_TYPES.POLLITEM),
    templateElement: z.boolean().optional(),
});
export type UnityPollItem = z.infer<typeof UnityPollItem>;

export const PlatePollQuestion = z.object({
    type: z.literal(ELEMENT_TYPES.POLL_QUESTION),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: PlateTextElements,
});

export type PollQuestionElement = z.infer<typeof PlatePollQuestion>;

export const PlatePollAnswer = z.object({
    type: z.literal(ELEMENT_TYPES.POLL_ANSWER),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: PlateTextElements,
});

export type PollAnswerElement = z.infer<typeof PlatePollAnswer>;

export const UnityPoll = z.object({
    type: z.literal(ELEMENT_TYPES.POLL),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    question: UnityPollItem,
    answers: z.array(UnityPollItem),
});
export type UnityPoll = z.infer<typeof UnityPoll>;

export const PlatePoll = z.object({
    type: z.literal(ELEMENT_TYPES.POLL),
    id: ElementId,
    data: z
        .object({
            templateElement: z.boolean().optional(),
        })
        .optional(),
    children: z.tuple([PlatePollQuestion], PlatePollAnswer),
});
export type PollElement = z.infer<typeof PlatePoll>;

export const PollCodec = z.codec(UnityPoll, PlatePoll, {
    decode: (value) => {
        const question: PollQuestionElement = {
            type: ELEMENT_TYPES.POLL_QUESTION,
            id: value.question.id ?? crypto.randomUUID(),
            ...(value.templateElement ? { data: { templateElement: value.templateElement } } : {}),
            children: TextElementsCodec.decode(value.question.text.items),
        };

        const answers: PollAnswerElement[] = value.answers.map((answer) => ({
            type: ELEMENT_TYPES.POLL_ANSWER,
            id: answer.id?.split('.')[1] ?? crypto.randomUUID(),
            ...(value.templateElement ? { data: { templateElement: value.templateElement } } : {}),
            children: TextElementsCodec.decode(answer.text.items),
        }));

        return {
            type: value.type,
            id: value.elementId ?? crypto.randomUUID(),
            ...(value.templateElement ? { data: { templateElement: value.templateElement } } : {}),
            children: [question, ...answers] satisfies [
                PollQuestionElement,
                ...PollAnswerElement[],
            ],
        };
    },
    encode: (value) => {
        const [plateQuestion, ...plateAnswers] = value.children;

        const questionId = plateQuestion.id ?? crypto.randomUUID();

        const question: UnityPollItem = {
            type: UNITY_ELEMENT_TYPES.POLLITEM,
            version: CURRENT_VERSION,
            text: {
                type: UNITY_ELEMENT_TYPES.TEXT,
                version: CURRENT_VERSION,
                items: TextElementsCodec.encode(plateQuestion.children),
            },
            ...(plateQuestion.data?.templateElement && {
                templateElement: plateQuestion.data.templateElement,
            }),
            id: questionId,
        };

        const answers: UnityPollItem[] = plateAnswers.map((plateAnswer) => ({
            type: UNITY_ELEMENT_TYPES.POLLITEM,
            version: CURRENT_VERSION,
            text: {
                type: UNITY_ELEMENT_TYPES.TEXT,
                version: CURRENT_VERSION,
                items: TextElementsCodec.encode(plateAnswer.children),
            },
            ...(plateAnswer.data?.templateElement && {
                templateElement: plateAnswer.data.templateElement,
            }),
            id: `${questionId}.${plateAnswer.id ?? crypto.randomUUID()}`,
        }));

        return {
            type: value.type,
            version: CURRENT_VERSION,
            question,
            answers,
            ...value.data,
            elementId: value.id,
        };
    },
});
